/**
 * Utility for mapping between new overview payload structure and existing slideout panel structure
 * Updated to work with dynamic form API configurations
 */

import { stripHtml } from 'app/components/SlideOutPanel/CommentPanel/utils';
import {
  FormApiResponse,
  extractOverviewConfig
} from './formApiConfigExtractor';

// New payload structure interface
export interface NewOverviewPayload {
  headline: string;
  productZen: string; // New field for Product Zen
  launchSummary: string;
  goal?: {
    option?: string;
    content?: string;
  };
  targetAudience: {
    options?: string[];
    option?: string;
    content?: string;
  };
  howIsItDifferent: string;
  launchRegion: string;
  audienceStrategicAlignment?: {
    audienceStrategyMappings: Record<string, string[]>;
    content: string;
  };
  mvpStatus: {
    option?: string;
    content?: string;
  };
}

// Existing slideout panel structure interface
export interface ExistingOverviewStructure {
  headline?: string;
  productZen?: string; // New field for Product Zen
  whatAreWeLaunching?: string;
  whyAreWeLaunching?: string | { selected: string[]; content: string }; // Support both simple dropdown and dropdown_richtext
  howIsItDifferent?: string;
  whereWillItLaunch?: string;
  audienceStrategicAlignment?: {
    audienceStrategyMappings: Record<string, string[]>;
    content: string;
  };
  status?: string;
  statusDescription?: string;
  mvpStatus?: { selected: string[]; content: string };
}

/**
 * Maps new overview payload structure to existing slideout panel structure.
 * Only includes keys that exist on the backend payload (sparse) so the UI can
 * "show only what was sent".
 */
export const mapNewOverviewToExisting = (
  newOverview: Partial<NewOverviewPayload>
): ExistingOverviewStructure => {
  const existing: ExistingOverviewStructure = {};
  const has = (key: keyof NewOverviewPayload | string) =>
    newOverview != null &&
    Object.prototype.hasOwnProperty.call(newOverview, key);

  if (has('headline')) {
    existing.headline = newOverview.headline;
  }
  if (has('howIsItDifferent')) {
    existing.howIsItDifferent = newOverview.howIsItDifferent;
  }
  if (has('launchSummary')) {
    existing.whatAreWeLaunching = newOverview.launchSummary;
  }
  if (has('launchRegion')) {
    existing.whereWillItLaunch = newOverview.launchRegion;
  }
  if (has('audienceStrategicAlignment')) {
    existing.audienceStrategicAlignment =
      newOverview.audienceStrategicAlignment;
  }

  if (has('goal')) {
    let goalData = newOverview.goal as any;

    if (typeof goalData === 'string') {
      try {
        goalData = JSON.parse(goalData);
      } catch (e) {
        console.warn('Failed to parse goal JSON string:', goalData);
        goalData = undefined;
      }
    }

    if (goalData && typeof goalData === 'object') {
      const option = goalData.option;
      const selectedValue =
        option && option !== '' && option !== null && option !== undefined
          ? String(option)
          : 'N/A';
      existing.whyAreWeLaunching = {
        selected: [selectedValue],
        content: goalData.content || ''
      };
    } else {
      existing.whyAreWeLaunching = {
        selected: ['N/A'],
        content: ''
      };
    }
  }

  if (has('productZen')) {
    existing.productZen = newOverview.productZen;
  }

  if (
    has('mvpStatus') &&
    newOverview.mvpStatus != null &&
    typeof newOverview.mvpStatus === 'object'
  ) {
    const ms = newOverview.mvpStatus as { option?: string; content?: string };
    existing.mvpStatus = {
      selected: (() => {
        if (ms.option && ms.option !== 'N/A' && ms.option !== '') {
          return [String(ms.option)];
        }
        return ['N/A'];
      })(),
      content: ms.content || ''
    };
  }

  return existing;
};

const SLIDEOUT_OVERVIEW_KEYS: (keyof ExistingOverviewStructure)[] = [
  'headline',
  'productZen',
  'whatAreWeLaunching',
  'whyAreWeLaunching',
  'howIsItDifferent',
  'whereWillItLaunch',
  'audienceStrategicAlignment',
  'status',
  'statusDescription',
  'mvpStatus'
];

/**
 * For overview objects already in slideout shape: keep only keys present on the payload.
 */
export const pickSentOverviewFields = (
  overview: Record<string, any>
): ExistingOverviewStructure => {
  const out: ExistingOverviewStructure = {};
  for (const key of SLIDEOUT_OVERVIEW_KEYS) {
    if (Object.prototype.hasOwnProperty.call(overview, key)) {
      (out as any)[key] = overview[key];
    }
  }
  return out;
};

/**
 * Maps existing slideout panel structure back to new overview payload structure
 */
export const mapExistingToNewOverview = (
  existingOverview: ExistingOverviewStructure
): Partial<NewOverviewPayload> => {
  const out: Partial<NewOverviewPayload> = {};
  const has = (key: keyof ExistingOverviewStructure) =>
    existingOverview != null &&
    Object.prototype.hasOwnProperty.call(existingOverview, key);

  if (has('headline')) {
    out.headline = existingOverview.headline || '';
  }
  if (has('howIsItDifferent')) {
    out.howIsItDifferent = existingOverview.howIsItDifferent || '';
  }
  if (has('whatAreWeLaunching')) {
    out.launchSummary = existingOverview.whatAreWeLaunching || '';
  }
  if (has('whereWillItLaunch')) {
    out.launchRegion = existingOverview.whereWillItLaunch || '';
  }
  if (has('audienceStrategicAlignment')) {
    out.audienceStrategicAlignment =
      existingOverview.audienceStrategicAlignment || {
        audienceStrategyMappings: {},
        content: ''
      };
  }

  if (has('whyAreWeLaunching')) {
    const w = existingOverview.whyAreWeLaunching;
    out.goal = w
      ? {
          option: typeof w === 'string' ? w : w.selected?.[0] || '',
          content:
            typeof w !== 'string' &&
            stripHtml(w.content || '').trim().length > 0
              ? w.content
              : ''
        }
      : { option: '', content: '' };
  }

  if (has('productZen')) {
    out.productZen = existingOverview.productZen || '';
  }

  if (has('mvpStatus') && existingOverview.mvpStatus != null) {
    out.mvpStatus = {
      option: existingOverview.mvpStatus.selected?.[0] || '',
      content:
        stripHtml(existingOverview.mvpStatus.content || '').trim().length > 0
          ? existingOverview.mvpStatus.content
          : ''
    };
  }

  return out;
};

/**
 * Helper function to check if overview data is in new format
 */
export const isNewOverviewFormat = (
  overview: any
): overview is NewOverviewPayload => {
  return (
    overview &&
    ('launchSummary' in overview ||
      ('goal' in overview && 'option' in overview.goal) ||
      ('targetAudience' in overview && 'options' in overview.targetAudience) ||
      ('mvpStatus' in overview && 'option' in overview.mvpStatus))
  );
};

/**
 * Universal overview adapter that handles both old and new formats
 */
export const adaptOverviewData = (overview: any): ExistingOverviewStructure => {
  if (!overview) {
    return {};
  }

  if (isNewOverviewFormat(overview)) {
    return mapNewOverviewToExisting(overview);
  }

  return pickSentOverviewFields(overview);
};

/**
 * Creates overview field configuration from form API response
 * This replaces static configurations with dynamic ones from the API
 */
export const createOverviewConfigFromFormApi = (
  formApiResponse: FormApiResponse,
  momentType: string = 'Product Release with Marketing'
) => {
  const overviewConfig = extractOverviewConfig(formApiResponse, momentType);

  // Convert form API config to slideout panel config format
  const slideoutConfig: Record<string, any> = {};

  Object.entries(overviewConfig).forEach(([fieldName, config]) => {
    // Map form API field names to slideout panel field names
    const slideoutFieldName = mapFormApiFieldNameToSlideout(fieldName);

    slideoutConfig[slideoutFieldName] = {
      type: mapFormApiFieldTypeToSlideout(config.type),
      options: config.options || [],
      required: config.required,
      label: config.label,
      api: config.api,
      charLimit: config.charLimit
    };
  });

  return slideoutConfig;
};

/**
 * Maps form API field names to slideout panel field names
 */
const mapFormApiFieldNameToSlideout = (formApiFieldName: string): string => {
  const fieldNameMapping: Record<string, string> = {
    launchSummary: 'whatAreWeLaunching',
    goal: 'whyAreWeLaunching',
    launchRegion: 'whereWillItLaunch',
    mvpStatus: 'mvpStatus',
    headline: 'headline',
    howIsItDifferent: 'howIsItDifferent',
    audienceStrategicAlignment: 'audienceStrategicAlignment'
    // whoIsThisFor: 'whoIsThisFor'
  };

  return fieldNameMapping[formApiFieldName] || formApiFieldName;
};

/**
 * Maps form API field types to slideout panel field types
 */
const mapFormApiFieldTypeToSlideout = (formApiType: string): string => {
  const typeMapping: Record<string, string> = {
    richtext: 'richtext',
    dropdown_richtext: 'dropdown_richtext',
    multi_select_richtext: 'multi_select_richtext',
    text: 'text',
    // Form API sometimes uses 'select' for simple dropdowns
    select: 'dropdown',
    // Be explicit if API already sends 'dropdown'
    dropdown: 'dropdown'
  };

  return typeMapping[formApiType] || formApiType;
};

/**
 * Adapts overview data with dynamic configuration from form API
 */
export const adaptOverviewDataWithFormApi = (
  overview: any,
  formApiResponse: FormApiResponse,
  momentType: string = 'Product Release with Marketing'
): ExistingOverviewStructure => {
  if (!overview) {
    return {};
  }

  // First check if it's in new format and adapt it
  if (isNewOverviewFormat(overview)) {
    return mapNewOverviewToExisting(overview);
  }

  return pickSentOverviewFields(overview);
};
